import React, { useState } from 'react';
import styled from 'styled-components';

const SliderContainer = styled.div`
  width: 100%;
  overflow: hidden;
  border-top: 1px solid
    ${({ darkMode }) => (darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(16, 29, 64, 0.07)')};
  background: ${({ theme, darkMode }) =>
    darkMode
      ? 'linear-gradient(180deg, rgba(12, 12, 18, 1) 0%, rgba(5, 5, 8, 0.98) 55%)'
      : `linear-gradient(180deg, #e8e6e0 0%, ${theme.colors.offWhite} 38%, ${theme.colors.offWhite} 100%)`};
  padding: clamp(2.25rem, 5vw, 3.75rem) 0 clamp(2.25rem, 5vw, 3.5rem);

  @media (max-width: 768px) {
    padding: clamp(1.75rem, 5vw, 2.75rem) 0 clamp(2rem, 5vw, 3rem);
  }
`;

const SlidesContainer = styled.div`
  display: flex;
  gap: 1.25rem;
  padding: 0 2rem;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 768px) {
    gap: 0.85rem;
    padding: 0 1rem;
  }
`;

/* Card: fixed aspect ratio, image fills it, text overlays bottom */
const Slide = styled.div.attrs(({ $darkMode }) => ({
  className: $darkMode ? 'bf-surface' : undefined
}))`
  flex-shrink: 0;
  position: relative;
  min-width: 380px;
  height: 280px;
  border-radius: 18px;
  overflow: hidden;
  cursor: pointer;
  border: 1px solid
    ${({ $darkMode }) =>
      $darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(16, 29, 64, 0.1)'};
  box-shadow: ${({ $darkMode }) =>
    $darkMode
      ? '0 20px 60px rgba(0,0,0,0.5)'
      : '0 8px 36px rgba(16, 29, 64, 0.14), 0 1px 0 rgba(255,255,255,0.7) inset'};

  @media (max-width: 768px) {
    min-width: 260px;
    height: 220px;
  }
`;

/* Full-bleed image */
const SlideImage = styled.img`
  display: block;
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
  user-select: none;
`;

/* Dark gradient rising from bottom */
const SlideOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    to top,
    rgba(10, 14, 30, 0.85) 0%,
    rgba(10, 14, 30, 0.45) 38%,
    rgba(10, 14, 30, 0) 68%
  );
  pointer-events: none;
`;

/* Title + button row, pinned to bottom */
const SlideFooter = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 0.85rem;
  padding: 1.1rem 1.25rem 1.15rem;
`;

const SlideTitle = styled.h3`
  flex: 1;
  min-width: 0;
  margin: 0;
  color: #fff;
  font-family: ${({ theme }) => theme.fonts.main};
  font-size: clamp(1.05rem, 2.2vw, 1.3rem);
  font-weight: 700;
  line-height: 1.2;
  text-shadow: 0 1px 6px rgba(0, 0, 0, 0.45);

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const RoundButton = styled.button`
  flex-shrink: 0;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: ${({ $darkMode, theme }) =>
    $darkMode ? 'linear-gradient(120deg, #f7c654, #ff9c3f)' : theme.colors.accent};
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  box-shadow:
    0 2px 0 rgba(255, 255, 255, 0.28) inset,
    0 4px 16px rgba(0, 0, 0, 0.35);

  &:focus-visible {
    outline: 2px solid rgba(255, 255, 255, 0.85);
    outline-offset: 3px;
  }

  svg {
    width: 18px;
    height: 18px;
    fill: currentColor;
  }

  @media (max-width: 768px) {
    width: 36px;
    height: 36px;

    svg {
      width: 15px;
      height: 15px;
    }
  }
`;

const ImageSlider = ({ slides = [], className, darkMode = false, intro = null }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const slidesRef = React.useRef(null);

  const handleMouseDown = e => {
    setIsDragging(true);
    setStartX(e.pageX - slidesRef.current.offsetLeft);
    setScrollLeft(slidesRef.current.scrollLeft);
  };

  const handleMouseUp = () => setIsDragging(false);

  const handleMouseMove = e => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - slidesRef.current.offsetLeft;
    slidesRef.current.scrollLeft = scrollLeft - (x - startX) * 2;
  };

  return (
    <SliderContainer className={className} darkMode={darkMode}>
      {intro}
      <SlidesContainer
        ref={slidesRef}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onMouseMove={handleMouseMove}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        {(slides || []).map((slide, index) => (
          <Slide key={index} $darkMode={darkMode} onClick={slide.action}>
            <SlideImage src={slide.image} alt={slide.title} draggable={false} />
            <SlideOverlay />
            <SlideFooter>
              <SlideTitle>{slide.title}</SlideTitle>
              <RoundButton
                type="button"
                onClick={e => { e.stopPropagation(); slide.action?.(); }}
                aria-label={`View ${slide.title}`}
                $darkMode={darkMode}
              >
                <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
                </svg>
              </RoundButton>
            </SlideFooter>
          </Slide>
        ))}
      </SlidesContainer>
    </SliderContainer>
  );
};

export default ImageSlider;
